/*
 * Lemonade Training Oy copy rights all right reserved.
 * Workout Type Definition
 */

import { Timestamp } from "firebase/firestore";

/* -------------- [ Enums and Unions ] ------------------ */
/**
 * Workout-level taxonomy offered to coaches when tagging a workout.
 *
 * These values are presented as tag options in the workout-authoring UI and
 * are persisted verbatim as plain strings inside `workoutData.tags` — they
 * are display labels matched by exact string equality, so values must never
 * carry stray whitespace. Historical workout docs may still carry
 * "Strength / Weightlifting " (trailing space) in their `tags` from before
 * the value was normalized; tag consumers should compare trimmed.
 */
export type WorkoutCategory =
  | "Strength / Weightlifting"
  | "Gymnastics / Skill / Accessory"
  | "Metcon / EMOM"
  | "Monostructural / Cardio"
  | "Test Workouts";

export const WORKOUT_CATEGORIES: WorkoutCategory[] = [
  "Strength / Weightlifting",
  "Gymnastics / Skill / Accessory",
  "Metcon / EMOM",
  "Monostructural / Cardio",
  "Test Workouts",
];

/* -------------- [ type definitions ] ------------------ */
export type Movement = {
  id?: number;

  /**
   * MovementRegistry entry id (global or box-scoped). The smart workout parser
   * uses this to look up the athlete's 1RM and personalize weights for
   * percentage-based prescriptions.
   */
  movementRegistryID: string;

  percent?: number;

  /** Number of sets in the prescription (e.g. the `5` in `5x3 @85%`). */
  sets?: number;

  /**
   * Reps per set in the prescription (e.g. the `3` in `5x3 @85%`). Allows a
   * string for non-numeric prescriptions like "AMRAP" or "max".
   */
  reps?: number | string;
};

export type WorkoutData = {
  title: string;
  description: string;
  date?: Timestamp;
  tags: string[];
  youTubeIDs: string[];
  movements?: Movement[];
  week?: number;
  day?: number | null;
  num?: number;

  // Deprecated
  youTubeID?: string | null;
};

export type Workout = {
  /**
   * Reserved workout-level category. No current code path writes or reads
   * this field — the category taxonomy reaches workouts only through
   * `workoutData.tags` (see `WorkoutCategory`). Kept for forward
   * compatibility with a first-class category on workout docs.
   */
  category?: WorkoutCategory;
  // -----------------------
  id: string;
  index?: number;
  CoachUID: string;
  workoutData: WorkoutData;
  hasRecordForThisWorkout: boolean;
  programmingUID: string;
  // deprecated
  UID?: string;
};

/* -------------- [ Workout Data Init ] ------------ */
export const WORKOUT_DATA_INIT: WorkoutData = {
  title: "",
  description: "",
  tags: [],
  youTubeIDs: [],
  movements: [],
};

/* -------------- [ Workout Results ] ------------ */
export type Like = {
  UID: string;
  name: string;
};

export interface WorkoutResult {
  id: string;
  athleteName: string | null;
  athleteUID: string;
  comments: string;
  workoutUID: string;
  likes: Like[];

  /**
   * Firestore server timestamp set when the result was first logged.
   * Optional on the type because legacy result docs may predate the field
   * (those would surface as `undefined` at read time). Used by the
   * journal/feed timeline for sorting and `where("createdAt", ">=", …)`
   * queries.
   */
  createdAt?: Timestamp;

  /**
   * Server timestamp written by the `submitWorkoutResults` Cloud Function.
   * Coexists with `createdAt`: score-list queries order by `timeStamp`,
   * journal/feed queries filter on `createdAt`, so the function writes both
   * (same instant). Absent on docs that predate the function.
   */
  timeStamp?: Timestamp;

  /** Duplicate of `athleteUID`, written server-side from the verified auth token. */
  userUID?: string;

  /** Box the result was logged under, when the workout belongs to a box programming. */
  boxID?: string;

  /**
   * Rating of perceived exertion, 0–10 scale, athlete-entered.
   * The mobile "Record Result" screen captures this as its primary score.
   */
  rpe?: number;

  /** Weight-based score in kilograms. */
  kg?: number;

  /** Repetition count score. */
  reps?: number;

  /** Generic numeric score for score-type workouts. */
  score?: number;

  /** Time-based score in seconds (seconds end-to-end across the platform). */
  time?: number;

  /**
   * Free-text note field written by older versions of the
   * `submitWorkoutResults` Cloud Function. `comments` is the canonical field
   * that result lists display; readers should fall back to `notes` for docs
   * from that era.
   */
  notes?: string;

  /** Score-type marker present on legacy mobile-written docs (e.g. ""). */
  scoreType?: string;

  /** Pre-formatted time display string on legacy mobile-written docs (e.g. "0:00"). */
  timeString?: string;
}

/* -------------- [ Todays workout ] ------------ */
export type TodaysWorkout = {
  id: string;
  name: string;
  date: Workout[];
};

/* -------------- [ Today's workout callables: requests ] ------------ */
/**
 * `userTodaysWorkout` request. `timezoneOffset` follows Date#getTimezoneOffset
 * (minutes, UTC minus local; Helsinki summer = -180). `timeZone` (IANA, e.g.
 * "Europe/Helsinki") is optional and additive: when valid, the server uses the
 * zone's own midnights, exact on 23- and 25-hour DST days; when absent or
 * unknown, the server uses the offset. Old clients send the offset only.
 */
export type TodaysWorkoutRequest = {
  /** Ignored by the server: it resolves the verified caller. Kept for old clients. */
  userUID?: string;
  timezoneOffset: number;
  /** F033 clients always send the GYM zone here, whatever clock they display. */
  timeZone?: string;
  /**
   * The caller's today ("YYYY-MM-DD") on the clock it displays. The server
   * rejects it (invalid-argument) unless isCalendarDateKey and
   * isPlausibleTodayKey hold, then windows that date on the gym's calendar.
   * Without a usable gym zone, and when absent, the server uses the legacy
   * today window from timeZone / timezoneOffset. Omit the key when there is no
   * date: the callable SDK sends undefined as null, which is rejected.
   */
  localDate?: string;
};

/** `getVisitCardTodaysWorkout` request; same day rules as {@link TodaysWorkoutRequest}. */
export type VisitCardTodaysWorkoutRequest = {
  timezoneOffset: number;
  timeZone?: string;
  localDate?: string;
};
